import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { find } from 'lodash';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Observer } from 'rxjs/Observer';

import { APP_CONFIG } from '../settings';
import { Country } from './models/country.model';
import { Language } from './models/language.model';

@Injectable()
export class InternalizationService {
  private languages$: BehaviorSubject<Language[]>;
  private countries$: BehaviorSubject<Country[]>;
  private selectedLang$: BehaviorSubject<Language>;
  private selectedCountry$: BehaviorSubject<Country>;
  private i18nConfig: any;

  constructor(private translateService: TranslateService, private http: HttpClient) {
    this.languages$ = new BehaviorSubject([]);
    this.countries$ = new BehaviorSubject([]);
    this.selectedLang$ = new BehaviorSubject(new Language());
    this.selectedCountry$ = new BehaviorSubject(new Country());
    this.i18nConfig = APP_CONFIG.i18n;
  }

  init(): Promise<any> {
    return new Promise((resolve) => {
      forkJoin(this.getInitializationMethods(this.i18nConfig))
        .subscribe(() => {
          if (this.i18nConfig.countries.enabled) {
            this.setSelectedCountry(this.getDefaultCountry());
            this.onCountryChange();
          }

          if (!this.i18nConfig.countries.useCountryLanguages) {
            const defaultLang = this.getDefaultLanguage();
            this.translateService.use(defaultLang);
            this.setSelectedLanguage(defaultLang);
          }

          this.onLanguageChange();

          return resolve(true);
        });

    });
  }

  getInitializationMethods(config) {
    if (config.countries.enabled && config.countries.useCountryLanguages) {
      this.handleEmptyListError(config.countries.list, 'Country');
      return [this.registerCountriesWithDefaultLanguages(config.countries.list)];
    } else if (config.countries.enabled) {
      this.handleEmptyListError(config.countries.list, 'Country');
      this.handleEmptyListError(config.languages.list, 'Languages');
      return [
        this.registerCountries(config.countries.list),
        this.registerLanguages(config.languages.list)
      ];
    } else {
      this.handleEmptyListError(config.languages.list, 'Languages');
      return [
        this.registerLanguages(config.languages.list)
      ];
    }
  }

  registerCountriesWithDefaultLanguages(countryCodes) {
    return Observable.create((observer: Observer<boolean>) => {
      forkJoin([
        this.http.get('assets/i18n/data/countries.json'),
        this.http.get('assets/i18n/data/languages.json')
      ]).subscribe((data: [Country[], Language[]]) => {
        const [countries, languages] = data;
        this.setCountries(this.initCountries(countries, countryCodes, { useDefaultLanguages: true, languages: languages }));
        observer.next(true);
        observer.complete();
      });
    });
  }

  registerCountries(countryCodes: string[]): Observable<Country[]> {
    return Observable.create((observer: Observer<boolean>) => {
      this.http.get('assets/i18n/data/countries.json')
        .subscribe(countries => {
          this.setCountries(this.initCountries(countries, countryCodes));
          observer.next(true);
          observer.complete();

        });
    });
  }

  registerLanguages(languageCodes: string[]): Observable<Language[]> {
    return Observable.create((observer: Observer<boolean>) => {
      this.http.get('assets/i18n/data/languages.json')
        .subscribe(languages => {
          this.setLanguages(this.initLanguages(languages, languageCodes));
          observer.next(true);
          observer.complete();
        });
    });
  }

  initCountries(countries, countryCodes, { useDefaultLanguages = false, languages = [] } = {}): Country[] {
    return Object.keys(countries)
      .reduce(($countries, countryCode) => {
        if (countryCodes.indexOf(countryCode) !== -1) {
          const country = { ...countries[countryCode] } as Country;
          country.shortCode = countryCode;
          if (useDefaultLanguages) {
            country.languages = this.initLanguages(languages, country.languages);
          }
          $countries.push(country);
        }
        return $countries;
      }, []);
  }

  initLanguages(languages, languageCodes): Language[] {
    return Object.keys(languages)
      .reduce(($languages, languageCode) => {
        if (languageCodes.indexOf(languageCode) !== -1) {
          const language = { ...languages[languageCode] } as Language;
          language.shortCode = languageCode;
          $languages.push(language);
        }
        return $languages;
      }, []);
  }

  getDefaultLanguage(): string {
    const languageFromUrl = this.getQueryParameterByName('language');
    const browserCultureLang = this.translateService.getBrowserCultureLang();

    let languageFromBrowser = find(APP_CONFIG.i18n.languages.list,
      (lang) => lang.toLowerCase() === browserCultureLang.replace('-', '_').toLowerCase());

    if (!(!!languageFromBrowser)) {
      if (browserCultureLang.indexOf('-') !== -1) {
        languageFromBrowser = find(APP_CONFIG.i18n.languages.list,
          (lang) => lang.toLowerCase() === browserCultureLang.split('-', 2)[0].toLowerCase());
      }
    }

    return languageFromUrl || languageFromBrowser || APP_CONFIG.i18n.defaultLanguage;
  }

  getDefaultCountry(): string {
    let countryFromBrowser = this.translateService.getBrowserCultureLang();
    if (countryFromBrowser.length > 2) {
      countryFromBrowser = countryFromBrowser.split('-', 2)[1];
    }
    const countryFromUrl = undefined;
    const countryFromCache = localStorage.getItem(APP_CONFIG.appPrefix + '.country');
    return countryFromUrl || countryFromCache || countryFromBrowser;
  }

  setCountries(countries: Country[]): void {
    this.countries$.next(countries);
  }

  setSelectedCountry(shortCode: string): void {
    this.selectedCountry$.next(find(this.getCountries(), { shortCode: shortCode }));
  }

  setLanguages(languages: Language[]): void {
    this.languages$.next(languages);
  }

  setSelectedLanguage(shortCode: string): void {
    this.selectedLang$.next(find(this.getLanguages(), { shortCode: shortCode }));
  }

  getCountries(): Country[] {
    return this.countries$.getValue();
  }

  getCountries$(): Observable<Country[]> {
    return this.countries$.asObservable();
  }

  getLanguages(): Language[] {
    return this.languages$.getValue();
  }

  getLanguages$(): Observable<Language[]> {
    return this.languages$.asObservable();
  }

  getSelectedLang(): Language {
    return this.selectedLang$.getValue();
  }

  getSelectedLang$(): Observable<Language> {
    return this.selectedLang$.asObservable();
  }

  getSelectedCountry(): Country {
    return this.selectedCountry$.getValue();
  }

  getSelectedCountry$(): Observable<Country> {
    return this.selectedCountry$.asObservable();
  }

  onLanguageChange(): void {
    this.getSelectedLang$()
      .subscribe((lang: Language) => {
        // localStorage.setItem(APP_CONFIG.appPrefix + '.lang', lang.shortCode);
        this.translateService.use(lang.shortCode);
      });
  }

  onCountryChange(): void {
    this.getSelectedCountry$()
      .subscribe((country: Country) => {

        // localStorage.setItem(APP_CONFIG.appPrefix + '.country', country.shortCode);

        if (this.i18nConfig.countries.useCountryLanguages) {
          this.setLanguages(country.languages);
          const languageFromCache = localStorage.getItem(APP_CONFIG.appPrefix + '.lang');
          !!languageFromCache ? this.setSelectedLanguage(languageFromCache) : this.setSelectedLanguage(country.languages[0].shortCode);
        }

      });
  }

  handleEmptyListError(list, type) {
    if (list.length === 0) {
      throw Error(`${type} list empty! Go to config/settings.ts to set the list of available ${type}.`);
    }
  }

  getQueryParameterByName(name, url = window.location.href) {
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

}



// WEBPACK FOOTER //
// ./src/config/i18n/i18n.service.ts